import TranslationService from '../contents/translation_service';
import PopoverService from '../contents/popover_service';
import BottomPopoverService from '../contents/bottom_popover_service';
import ChatService from './chat/chat_service';
import showMyPathScenario from './chat/my_path_scenario';
import '../my_path/scenario_loading.css';
import ChatOptions from './chat/chat_options';
import AudioRecordingService from './chat/audio_recording_service';
import ChatTopicService from './chat/chat_topic_service';
import AudioPlayerService from './chat/audio_player_service';
import AudioPlayer from './chat/audio_player';
import TextCorrectionService from './chat/text_correction_service';
import CorrectionExplanationService from './chat/correction_explanation_service';
import ChatTranslationService from './chat/chat_translation_service';
import AlternativeResponseService from './chat/alternative_response_service';
import SuggestedReplyService from './chat/suggested_reply_service';
import SlowAudioService from './chat/slow_audio_service';
import TranslatorModalService from './chat/translator_modal_service';
import ChatFeedbackModalService from "./chat/chat_feedback_modal_service";
import GuidedCourseConfigService from './chat/guided_course_config_service';

const WORKER_CONTEXT_SETTING_KEYS = new Set([
  'ai_model',
  'aiModel',
  'autoCorrect',
  'clientProvider',
  'guidedMode',
  'model',
  'repeatCorrections',
  'speedMultiplier',
  'verbalCorrectionMode',
  'voiceModel'
]);

function shouldRefreshWorkerContext(updatedOptions = {}) {
  return Object.keys(updatedOptions).some((key) => WORKER_CONTEXT_SETTING_KEYS.has(key));
}

document.addEventListener('turbolinks:load', function() {
  const chatForm = document.getElementById('chat-form');

  if (chatForm) {
    const chatInput = document.getElementById('js-chat-input');
    const chatTopic = document.getElementById('js-chat-topic');
    const chatTopicCategory = document.getElementById('js-chat-topic-category');
    const chatSubtopicCategory = document.getElementById('js-chat-subtopic-category');

    const chatId = chatForm.dataset.contentId;
    const contentType = chatForm.dataset.contentType;
    const language = chatForm.dataset.language;
    const languageLabel = chatForm.dataset.languageLabel;
    const languageCode = chatForm.dataset.languageCode;
    const targetLanguage = chatForm.dataset.targetLanguage;
    const dialectCode = chatForm.dataset.dialectCode;
    const mode = chatForm.dataset.mode;
    const voice = chatForm.dataset.voice;
    const voiceEnabled = chatForm.dataset.voiceEnabled === 'true';
    const voiceProvider = chatForm.dataset.voiceProvider;
    const ttsModel = chatForm.dataset.ttsModel || null;
    const voiceSpeed = chatForm.dataset.voiceSpeed;
    const voiceGender = chatForm.dataset.voiceGender;
    const streamingEnabled = chatForm.dataset.streamingEnabled;
    const autoRecord = chatForm.dataset.autoRecord;
    const autoSend = chatForm.dataset.autoSend;
    const autoCorrect = chatForm.dataset.autoCorrect;
    const repeatCorrections = chatForm.dataset.repeatCorrections;
    const verbalCorrectionMode = chatForm.dataset.verbalCorrectionMode;
    const autoTranslate = chatForm.dataset.autoTranslate;
    const guidedMode = chatForm.dataset.guidedMode;
    const debug = chatForm.dataset.debug;
    const messageCount = chatForm.dataset.messageCount;
    const translationProvider = 'deepl';
    const hideTranslationSentence = false;
    const model = chatForm.dataset.model;
    const clientProvider = chatForm.dataset.clientProvider;
    const transcriptionMode = chatForm.dataset.transcriptionMode;
    const aiModel = chatForm.dataset.aiModel;
    const voiceModel = chatForm.dataset.voiceModel;
    const premiumUser = chatForm.dataset.premiumAccount;
    const pronunciationCharacters = chatForm.dataset.pronunciationCharacters;
    const pronunciationCharactersOptions = chatForm.dataset.pronunciationCharactersOptions;
    const speedMultiplier = chatForm.dataset.speedMultiplier;
    const speedSliderEnabled = chatForm.dataset.speedSliderEnabled === 'true';
    const disableGuidedMode = chatTopicCategory && (chatTopicCategory.value === 'vocab' || chatTopicCategory.value === 'grammar' || chatTopicCategory.value === 'guided');
    const highlightMode = chatForm.dataset.highlightMode;
    const transcriptionUrl = chatForm.dataset.transcriptionUrl;
    const userUuid = chatForm.dataset.userUuid;
    const workerEnabled = chatForm.dataset.workerEnabled === 'true';
    const workerFeatureEnabled = chatForm.dataset.workerFeatureEnabled === 'true';
    const correctionModeSettingsEnabled = chatForm.dataset.correctionModeSettingsEnabled === 'true';
    const verbalCorrectionOptionsEnabled = chatForm.dataset.verbalCorrectionOptionsEnabled === 'true';

    const streamLanguageLevel = chatForm.dataset.streamLanguageLevel;
    const isBeginnerOrBasic = ['beginner', 'basic'].includes(streamLanguageLevel);

    // Topic category detection now uses GuidedCourseConfigService for consistency with backend
    // These are the initial values from the DOM; GuidedCourseConfigService can provide dynamic updates
    const topicCategoryValue = chatTopicCategory ? chatTopicCategory.value : '';
    const isGrammarChat = topicCategoryValue.startsWith('grammar');
    const isGuidedLesson = topicCategoryValue === 'guided';
    const isVocabChat = topicCategoryValue === 'vocab';
    // Disable AI model selection for grammar chats and guided lessons (per GuidedCourseService rules)
    const disableAiModelSelection = isGrammarChat || isGuidedLesson;

    const options = {
      chatId: chatId,
      language: language,
      languageLabel: languageLabel,
      languageCode: languageCode,
      targetLanguage: targetLanguage,
      mode: mode,
      voice: voice,
      voiceEnabled: voiceEnabled,
      voiceProvider: voiceProvider,
      voiceGender: voiceGender,
      voiceSpeed: voiceSpeed,
      speedMultiplier: speedMultiplier,
      streamingEnabled: streamingEnabled === 'true',
      autoRecord: autoRecord === 'true',
      autoSend: autoSend === 'true',
      autoCorrect: autoCorrect === 'true',
      repeatCorrections: repeatCorrections === 'true',
      verbalCorrectionMode: verbalCorrectionMode || null,
      autoTranslate: autoTranslate,
      guidedMode: guidedMode === 'true',
      debug: debug,
      messageCount: messageCount,
      model: model,
      clientProvider: clientProvider,
      transcriptionMode: transcriptionMode,
      aiModel: aiModel,
      dialectCode: dialectCode,
      voiceModel: voiceModel,
      beta_features_enabled: chatForm.dataset.betaFeaturesEnabled,
      pronunciationCharacters: pronunciationCharacters,
      pronunciationCharactersOptions: pronunciationCharactersOptions,
      disableGuidedMode: disableGuidedMode,
      speedSliderEnabled: speedSliderEnabled,
      streamLanguageLevel: streamLanguageLevel,
      isBeginnerOrBasic: isBeginnerOrBasic,
      highlightMode: highlightMode,
      transcriptionUrl: transcriptionUrl,
      userUuid: userUuid,
      workerEnabled: workerEnabled,
      workerFeatureEnabled: workerFeatureEnabled,
      aiModelOptions: chatForm.dataset.aiModelOptions,
      aiModelHelpText: chatForm.dataset.aiModelHelpText,
      disableAiModelSelection: disableAiModelSelection,
      correctionModeSettingsEnabled: correctionModeSettingsEnabled,
      verbalCorrectionOptionsEnabled: verbalCorrectionOptionsEnabled,
      ttsModel,
      voxtralDefaultEnabled: chatForm.dataset.voxtralDefaultEnabled === 'true',
      sonioxDefaultEnabled: chatForm.dataset.sonioxDefaultEnabled === 'true',
      sonioxRealtimeEnabled: chatForm.dataset.sonioxRealtimeEnabled === 'true',
      transcriptionCleanup: chatForm.dataset.transcriptionCleanup === 'true',
      multipleChoiceExercise: chatForm.dataset.multipleChoiceExercise === 'true',
      transcriptionPostProcessingEnabled: chatForm.dataset.transcriptionPostProcessingEnabled === 'true'
    };

    const chatOptions = new ChatOptions(options);

    // Audio recording variables
    const chatElementContainer = document.querySelector('.chat-content-w');
    const chatElement = document.querySelector('.chat-content');
    showMyPathScenario(chatId, chatElement);
    const chatControls = document.querySelector('.chat-controls');
    const chatSubmitBtn = document.getElementById('js-chat-submit-btn');
    const recordBtn = document.getElementById("js-record-query-btn"); // Make sure this ID matches your HTML button's ID
    const pauseBtn = document.getElementById("js-pause-recording-btn"); // Make sure this ID matches your HTML button's ID
    const micIcon = document.querySelector(".fa.fa-microphone"); // Updated class selector

    const csrfToken = $('meta[name="csrf-token"]').attr('content');
    const premiumAccount = premiumUser;

    const translationService = new TranslationService(translationProvider, language, targetLanguage, csrfToken);

    const popoverService = new BottomPopoverService(translationService, language, chatId, contentType, csrfToken, 'true', hideTranslationSentence, null, voice, voiceProvider);

    const audioPlayer = new AudioPlayer();
    const audioPlayerService = new AudioPlayerService(csrfToken);
    const chatTopicService = new ChatTopicService(chatElementContainer, chatElement);
    const textCorrectionService = new TextCorrectionService(csrfToken);
    const correctionExplanationService = new CorrectionExplanationService(csrfToken);
    const chatTranslationService = new ChatTranslationService(csrfToken);
    const alternativeResponseService = new AlternativeResponseService(csrfToken);
    const suggestedReplyService = new SuggestedReplyService(csrfToken, dialectCode);
    const slowAudioService = new SlowAudioService(csrfToken);
    const feedbackModalService = new ChatFeedbackModalService(csrfToken);
    const translatorModalService = new TranslatorModalService(csrfToken);
    const chatService = new ChatService(chatId, chatOptions, chatElementContainer, chatElement, chatControls, chatForm, chatInput, chatTopicCategory, chatSubtopicCategory, chatTopic, chatSubmitBtn, popoverService, translationService, audioPlayerService, chatTopicService, textCorrectionService, correctionExplanationService, chatTranslationService, alternativeResponseService, suggestedReplyService, slowAudioService, translatorModalService, feedbackModalService, pronunciationCharacters);
    chatOptions.onSettingsUpdated = ({ updatedOptions } = {}) => {
      if (shouldRefreshWorkerContext(updatedOptions)) {
        chatService.refreshConnectionContext();
      }
    };
    chatService.audioPlayer = audioPlayer;
    const audioRecordingService = new AudioRecordingService(csrfToken, chatService, audioPlayerService, recordBtn, micIcon, pauseBtn, chatOptions);

    if (chatSubmitBtn) {
      chatService.init();
    } else {
      chatService.setupEventListeners();
    }

    if (recordBtn) {
      audioRecordingService.setup({
        deferPermission: chatForm?.dataset.serverMediatedAi === 'true' && chatOptions.mode === 'text_only'
      });

      // Release the microphone stream and the AudioContext when leaving the page,
      // otherwise each visit stacks another context against the browser's cap.
      document.addEventListener('turbolinks:before-cache', function teardownRecording() {
        document.removeEventListener('turbolinks:before-cache', teardownRecording);
        Promise.resolve(audioRecordingService.teardown()).catch((error) => {
          console.error('Error tearing down audio recording service:', error);
        });
      });

      import(/* webpackChunkName: "nosleep" */ 'nosleep.js').then(module => {
        const NoSleep = module.default;
        const noSleep = new NoSleep();

        // To enable wake lock:
        // // (must be wrapped in a user input event handler e.g. a mouse or touch handler)
        document.addEventListener('click', function enableNoSleep() {
          document.removeEventListener('click', enableNoSleep, false);
          if (chatForm?.dataset.serverMediatedAi === 'true') {
            // Wake lock is optional; denial must not create an unhandled
            // rejection when a learner clicks a normal chat control.
            Promise.resolve().then(() => noSleep.enable()).catch(() => {});
          } else {
            noSleep.enable();
          }
        }, false);
      });
    }

    chatOptions.audioRecordingService = audioRecordingService;
    chatOptions.audioPlayerService = audioPlayerService;

    // Initialize GuidedCourseConfigService for dynamic UI configuration
    const guidedCourseConfigService = new GuidedCourseConfigService(csrfToken);
    chatOptions.guidedCourseConfigService = guidedCourseConfigService;

    // Connect GuidedCourseConfigService to ChatOptions for runtime updates
    guidedCourseConfigService.setChatOptions(chatOptions);

    // Fetch guided course config if needed (for guided, grammar, or vocab topics)
    if (isGuidedLesson || isGrammarChat || isVocabChat) {
      guidedCourseConfigService.fetchConfig(topicCategoryValue, {
        voice_provider: voiceProvider,
        voice: voice,
        mode: mode
      }).then(config => {
        // Apply UI configuration based on backend rules
        guidedCourseConfigService.applyToUI();

        // Make config available to chat service for language-specific handling
        if (chatService && config.language_config) {
          chatService.languageConfig = config.language_config;
        }
      }).catch(error => {
        console.warn('Could not fetch guided course config:', error);
      });
    }
  }

  const aiModelSelects = document.querySelectorAll('select[data-ai-model-options]');

  aiModelSelects.forEach((selectElement) => {
    const rawOptions = selectElement.dataset.aiModelOptions;
    const helpTargetId = selectElement.dataset.aiModelHelpTarget;
    const helpElement = helpTargetId ? document.getElementById(helpTargetId) : null;

    if (!rawOptions || !helpElement) {
      return;
    }

    let parsedOptions;

    try {
      parsedOptions = typeof rawOptions === 'string' ? JSON.parse(rawOptions) : rawOptions;
    } catch (error) {
      console.warn('Unable to parse AI model options for help text.', error);
      parsedOptions = [];
    }

    if (!Array.isArray(parsedOptions)) {
      parsedOptions = [];
    }

    const helpTextMap = parsedOptions.reduce((accumulator, option) => {
      if (option && option.value) {
        accumulator[option.value] = option.helpText || option.help_text || '';
      }
      return accumulator;
    }, {});

    const updateHelpText = (value) => {
      const helpText = helpTextMap[value] || '';
      helpElement.textContent = helpText;
      helpElement.style.display = helpText ? '' : 'none';
    };

    updateHelpText(selectElement.value);

    selectElement.addEventListener('change', (event) => {
      updateHelpText(event.target.value);
    });
  });
});
